import { useEffect, useState } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { Header, Navbar } from './containers'
import { useAuth, useReportsContext, useUser } from './context'
import ProtectedRoute from './components/ProtectedRoute'
import { Box, Modal } from '@mui/material'

import {
    AppliancePage,
    LoginPage,
    MachinesPage,
    WelcomePage,
    ResetPasswordPage,
    ReportsPage,
    AlertPage,
    InfoPage,
    ConfigurationPage,
    SitesPage,
    AdminPage,
    MaintenancePage,
    MachinesListPage,
    AdminUsersPage,
    AdminNewUserPage,
    CreateReports,
    DeletedReportsPage,
    NoPermissionPage,
    RestorePasswordPage,
    ActivitiesDetailPage,
    ActivitiesPage,
    InternalMessagesPage,
    NotificationsPage,
    OptionsPage,
    CalendarPage,
    WallJournalPage,
    TestPage
} from './pages'
import { MachinesAdminPage, PatternsPage, RolesPage, UserProfilePage, GaleryPage, AdminCronsSyncPage, AdminManualsPage } from './pages/administration'
import { MyWallPage } from './pages/wall-journal'
import { DispositivosPage } from './pages/administration/Dispositivos.Page'
import { Device } from './pages/device/Device.page'
import { GenerationStatusModal } from './dialogs/StatusModal'

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}

const AppRouter = () => {
    const {
        loading,
        isAuthenticated,
        logout,
        justLoggedIn,
        clearJustLoggedInFlag
    } = useAuth()

    const {
        admin,
        isOperator,
        isSapExecutive,
        isSuperAdmin,
        isShiftManager,
        isChiefMachinery,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector,
        isObserver
    } = useUser()

    const { initiateEndAllShifts } = useReportsContext()

    useEffect(() => {
        if (justLoggedIn && isOperator && !loading) {
            console.log('[AppRouter] User has just logged in as an operator. Checking for open shifts.')
            initiateEndAllShifts(false)
            clearJustLoggedInFlag()
        }
    }, [justLoggedIn, isOperator, loading, initiateEndAllShifts, clearJustLoggedInFlag])

    const [permissionError, setPermissionError] = useState(null)

    useEffect(() => {
        const handleApiError403 = (event) => {
            setPermissionError(event.detail)
        }

        window.addEventListener('apiError403', handleApiError403)

        return () => {
            window.removeEventListener('apiError403', handleApiError403)
        }
    }, [])

    const handleClearErrorAndLogout = () => {
        setPermissionError(null)
        logout()
    }

    if (loading) {
        return null // Or a loading spinner
    }

    return (
        <div id={'root1'} style={{ fontFamily: 'Roboto' }}>
            <GenerationStatusModal />
            {isAuthenticated && <Navbar/>}
            {isAuthenticated && <Header/>}
            <Routes>
                <Route path={'/device/:deviceId'} element={<Device />} />

                {/* Public Routes */}
                {!isAuthenticated && (
                    <>
                        <Route exact path={'/reset-password'} element={<ResetPasswordPage />} />
                        <Route exact path={'/restore-password/:id'} element={<RestorePasswordPage />} />
                        <Route path={'/login'} element={<LoginPage />} />
                    </>
                )}

                <Route path='/' element={<Navigate to={!isAuthenticated ? '/login' : '/welcome'} />} />

                {/* Protected Routes */}
                <Route path='/welcome' element={<ProtectedRoute isAllowed={isAuthenticated} redirectTo="/login"><WelcomePage /></ProtectedRoute>} />

                <Route path={'/internal-messages'} element={
                    <ProtectedRoute isAllowed={admin || isSapExecutive}>
                        <InternalMessagesPage route={'internal-messages'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/reports' element={<ProtectedRoute isAllowed={isAuthenticated && (admin || isSapExecutive || isShiftManager || isChiefMachinery || isOperatorInspector || isUniversalChief || isUniversalInspector || isObserver)}><ReportsPage route='reports'/></ProtectedRoute>} />
                <Route exact path='/reports/create-report' element={<ProtectedRoute isAllowed={isAuthenticated && (admin || isSapExecutive || isShiftManager || isChiefMachinery || isOperatorInspector || isUniversalChief || isUniversalInspector || isObserver)}><CreateReports route='reports'/></ProtectedRoute>} />
                <Route exact path='/reports/deleted' element={<ProtectedRoute isAllowed={isAuthenticated && (admin || isSuperAdmin)}><DeletedReportsPage /></ProtectedRoute>} />
                <Route exact path='/reports/edit-report/:id' element={<ProtectedRoute isAllowed={isAuthenticated && (admin || isSapExecutive || isShiftManager || isChiefMachinery || isOperatorInspector || isUniversalChief || isUniversalInspector || isObserver)}><CreateReports route='reports'/></ProtectedRoute>} />
                <Route exact path='/alerts' element={<ProtectedRoute isAllowed={isAuthenticated}><AlertPage route={'alerts'}/></ProtectedRoute>} />
                <Route exact path='/user-profile' element={<ProtectedRoute isAllowed={isAuthenticated}><UserProfilePage route={'user-profile'}/></ProtectedRoute>} />
                <Route exact path='/info' element={<ProtectedRoute isAllowed={isAuthenticated}><InfoPage route={'info'}/></ProtectedRoute>}/>

                <Route exact path='/machines' element={<ProtectedRoute isAllowed={isAuthenticated && (!isOperator || isUniversalInspector || isUniversalChief || isOperatorInspector || isObserver)}><MachinesPage route={'machines'}/></ProtectedRoute>} />
                <Route exact path='/machines/:id' element={<ProtectedRoute isAllowed={isAuthenticated && (!isOperator || isUniversalInspector || isUniversalChief || isOperatorInspector || isObserver)}><MachinesListPage route='machines'/></ProtectedRoute>} />
                <Route exact path='/machines/:el/machine-detail/:id' element={<ProtectedRoute isAllowed={isAuthenticated && (!isOperator || isUniversalInspector || isUniversalChief || isOperatorInspector || isObserver)}><AppliancePage route={'machines/machine-detail'}/></ProtectedRoute>} />

                <Route exact path='/maintenance' element={
                    <ProtectedRoute isAllowed={isAuthenticated && (isOperator || isUniversalInspector || isOperatorInspector)}>
                        <MaintenancePage route={'maintenance'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/sites' element={
                    <ProtectedRoute isAllowed={isAuthenticated && (isSapExecutive || admin || isSuperAdmin || isUniversalChief || isUniversalInspector || isObserver)}>
                        <SitesPage route={'sites'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/configuration' element={<ProtectedRoute isAllowed={isAuthenticated}><ConfigurationPage route={'configuration'}/></ProtectedRoute>}/>
                <Route exact path='/options' element={<ProtectedRoute isAllowed={isAuthenticated}><OptionsPage route={'options'}/></ProtectedRoute>} />

                <Route exact path='/administration' element={
                    <ProtectedRoute isAllowed={isAuthenticated && admin}>
                        <AdminPage route={'administration'} />
                    </ProtectedRoute>
                } />

                <Route exact path='/users' element={
                    <ProtectedRoute isAllowed={isAuthenticated && (isSapExecutive || admin)}>
                        <AdminUsersPage route={'users'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/machinesAdmin' element={
                    <ProtectedRoute isAllowed={isAuthenticated && (isSapExecutive || admin)}>
                        <MachinesAdminPage />
                    </ProtectedRoute>
                } />

                <Route exact path='/new-users' element={
                    <ProtectedRoute isAllowed={isAuthenticated && (isSapExecutive || admin)}>
                        <AdminNewUserPage route={'new-users'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/roles' element={
                    <ProtectedRoute isAllowed={isAuthenticated && isSuperAdmin}>
                        <RolesPage route={'roles'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/gallery' element={
                    <ProtectedRoute isAllowed={isAuthenticated && isSuperAdmin}>
                        <GaleryPage route={'gallery'}/>
                    </ProtectedRoute>
                } />

                <Route exact path='/patterns' element={<ProtectedRoute isAllowed={isAuthenticated && (!isOperator || isUniversalInspector || isUniversalChief || isOperatorInspector)}><PatternsPage route={'roles'}/></ProtectedRoute>}/>
                <Route exact path='/edit-user/:id' element={<ProtectedRoute isAllowed={isAuthenticated && (isSapExecutive || admin)}><AdminNewUserPage route={'edit-user'}/></ProtectedRoute>} />
                <Route path='/no-permission' element={<ProtectedRoute isAllowed={isAuthenticated}><NoPermissionPage /></ProtectedRoute>} />
                <Route path='/assignment/:id' element={<ProtectedRoute isAllowed={isAuthenticated}><ActivitiesDetailPage route={'assignment'}/></ProtectedRoute>} />
                <Route exact path='/assignment' element={<ProtectedRoute isAllowed={isAuthenticated}><ActivitiesPage route={'assignment'}/></ProtectedRoute>} />
                <Route exact path='/notifications' element={<ProtectedRoute isAllowed={isAuthenticated}><NotificationsPage route={'notifications'}/></ProtectedRoute>} />
                <Route exact path='/calendar' element={<ProtectedRoute isAllowed={isAuthenticated}><CalendarPage route={'calendar'}/></ProtectedRoute>} />
                <Route exact path='/news' element={<ProtectedRoute isAllowed={isAuthenticated && (admin || isSapExecutive)}><WallJournalPage/></ProtectedRoute>} />
                <Route exact path='/mynews' element={<ProtectedRoute isAllowed={isAuthenticated}><MyWallPage/></ProtectedRoute>} />
                <Route exact path='/dispositivos' element={<ProtectedRoute isAllowed={isAuthenticated && isSuperAdmin}><DispositivosPage/></ProtectedRoute>} />
                <Route exact path='/admin-crons-sync' element={<ProtectedRoute isAllowed={isAuthenticated && isSuperAdmin}><AdminCronsSyncPage/></ProtectedRoute>} />
                <Route exact path='/admin-manuals' element={<ProtectedRoute isAllowed={isAuthenticated && isSuperAdmin}><AdminManualsPage/></ProtectedRoute>} />
                <Route exact path='/test' element={<ProtectedRoute isAllowed={isAuthenticated && isSuperAdmin}><TestPage/></ProtectedRoute>} />
            </Routes>

            <Modal open={!!permissionError} onClose={() => setPermissionError(null)}>
                <Box sx={modalStyle}>
                    <NoPermissionPage
                        error={permissionError}
                        onClear={() => setPermissionError(null)}
                        onLogout={handleClearErrorAndLogout}
                    />
                </Box>
            </Modal>
        </div>
    )
}

export default AppRouter
